import React from "react";
import { FileText, Video, Play, MessageSquareText, Mic, Check } from "lucide-react";

/* DOM-built product vignettes - real UI recreations, no images, no invented user results. */

const Panel = ({ label, children, className = "", testid, num }) => (
  <div data-testid={testid} className={`rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden hover:border-amber-500/25 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.8)] transition-[border-color,transform,box-shadow] duration-300 ${className}`}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-800/80 bg-zinc-950/40">
      <span className="h-2 w-2 rounded-full bg-zinc-700" />
      <span className="h-2 w-2 rounded-full bg-zinc-700" />
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 ml-2">{label}</span>
      {num && <span className="ml-auto font-mono text-[10px] text-zinc-600 tabular-nums">{num}</span>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Bar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[11px] mb-1">
      <span className="text-zinc-400">{label}</span>
      <span className="text-zinc-200 font-semibold tabular-nums">{value}</span>
    </div>
    <div className="h-1.5 rounded-full bg-zinc-800">
      <div className="h-1.5 rounded-full bg-amber-500/80" style={{ width: `${value}%` }} />
    </div>
  </div>
);

export const JobMatchVignette = () => (
  <Panel label="Stand Out · Job Match" num="01" testid="vignette-job-match" className="h-full">
    <div className="flex items-center gap-4 mb-4">
      <div className="h-14 w-14 rounded-full border-2 border-amber-500/70 flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-semibold text-amber-400 tabular-nums">78</span>
      </div>
      <div>
        <div className="text-sm text-zinc-100 font-medium">Job Match Score</div>
        <div className="text-[11px] text-zinc-500">Your resume vs. this job description</div>
      </div>
    </div>
    <div className="space-y-3">
      <Bar label="Required skills" value={82} />
      <Bar label="Relevant experience" value={74} />
      <Bar label="ATS keyword coverage" value={79} />
    </div>
    <div className="mt-4 font-mono text-[10px] text-zinc-500 bg-zinc-950/60 border border-zinc-800 rounded-lg px-3 py-2 truncate">
      missing keywords: <span className="text-amber-400">stakeholder management</span> · <span className="text-amber-400">roadmap</span>
    </div>
  </Panel>
);

export const PredictedQuestionsVignette = () => (
  <Panel label="Predicted Interview Questions" num="02" testid="vignette-predicted-questions" className="h-full">
    <ul className="space-y-2.5">
      {[
        { tag: "From your resume", q: "Your resume says you grew activation 32% at Loopwork — walk me through what actually drove that number." },
        { tag: "Resume × job description", q: "This role owns a B2B roadmap, but most of your experience is B2C — how would you adapt your discovery process?" },
        { tag: "Career signal", q: "You moved from engineering into product in 2023 — what convinced you, and how does that background shape your PM style?" },
      ].map(({ tag, q }, i) => (
        <li key={i} className="flex gap-3 items-start">
          <span className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-md bg-zinc-800/70 text-[10px] text-amber-400 font-semibold flex items-center justify-center">{i + 1}</span>
          <span className="min-w-0">
            <span className="block text-[9px] uppercase tracking-[0.18em] text-amber-400/80 mb-0.5">{tag}</span>
            <span className="block text-[13px] text-zinc-300 leading-snug">{q}</span>
          </span>
        </li>
      ))}
    </ul>
    <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-zinc-600">
      Derived from your resume + this job description
    </div>
  </Panel>
);

export const MockInterviewVignette = () => (
  <Panel label="Practice Interview" num="03" testid="vignette-mock-interview" className="h-full">
    <div className="rounded-xl bg-zinc-950 border border-zinc-800 aspect-video flex flex-col items-center justify-center gap-3">
      <div className="h-12 w-12 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center">
        <Video className="h-5 w-5 text-zinc-400" />
      </div>
      <div className="flex items-center gap-2 text-[11px] text-zinc-500">
        <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" /> Recording your answer
      </div>
    </div>
    <div className="mt-3 flex items-center gap-2 text-[12px] text-zinc-400">
      <Mic className="h-3.5 w-3.5 text-amber-400" />
      "This role owns a B2B roadmap, but most of your experience is B2C — how would you adapt your discovery process?"
    </div>
  </Panel>
);

export const ReplayVignette = () => (
  <Panel label="Interview Replay & Feedback" testid="vignette-replay">
    <div className="flex items-center gap-3 mb-3">
      <span className="h-8 w-8 rounded-full bg-amber-500 flex items-center justify-center">
        <Play className="h-3.5 w-3.5 text-zinc-950 ml-0.5" />
      </span>
      <div className="flex-1 h-1.5 rounded-full bg-zinc-800">
        <div className="h-1.5 rounded-full bg-amber-500/80 w-1/3" />
      </div>
      <span className="text-[10px] text-zinc-500 tabular-nums">0:42 / 2:10</span>
    </div>
    <div className="space-y-2">
      <div className="flex gap-2 items-start">
        <MessageSquareText className="h-3.5 w-3.5 text-zinc-500 mt-0.5 flex-shrink-0" />
        <p className="text-[12px] text-zinc-400 leading-snug font-mono">
          "…so I re-scoped the launch into two phases and aligned the team…"
        </p>
      </div>
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.05] px-3 py-2 text-[11px] text-amber-200/90 leading-snug">
        Coach: strong situation setup, quantify the outcome to complete the STAR arc.
      </div>
    </div>
  </Panel>
);

export const OfficialInterviewVignette = () => (
  <Panel label="Official AI Interview" num="04" testid="vignette-official-interview" className="h-full">
    <div className="relative rounded-xl overflow-hidden border border-zinc-800 aspect-video">
      <img src="/avatars/hiring_manager_base.webp" alt="AI interviewer" loading="lazy"
           className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 20%" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 50% 30%, transparent 55%, rgba(9,9,11,0.65) 100%)" }} />
      <span className="absolute top-2 left-2 inline-flex items-center rounded-full border border-amber-500/40 bg-zinc-950/70 backdrop-blur-sm px-2 py-0.5 text-[8px] font-semibold uppercase tracking-widest text-amber-300">
        Official assessment
      </span>
      <span className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/15 backdrop-blur-sm px-2 py-0.5 text-[8px] font-semibold uppercase tracking-widest text-amber-300">
        <span className="h-1 w-1 rounded-full bg-amber-400 animate-pulse" /> Speaking
      </span>
      <div className="absolute left-2.5 bottom-2">
        <div className="text-[11px] font-semibold text-zinc-50">Daniel Mercer</div>
        <div className="text-[9px] text-zinc-400">Line Manager · AI Interviewer</div>
      </div>
    </div>
    <div className="mt-3 flex items-center gap-2">
      <span className="text-[9px] uppercase tracking-[0.16em] text-zinc-500 flex-shrink-0">Choose your interviewer</span>
      <div className="flex items-center gap-1.5 ml-auto">
        {["hr_officer", "executive", "recruiter", "tech_lead"].map((p) => (
          <span key={p} className="h-6 w-6 rounded-full overflow-hidden ring-1 ring-zinc-700 flex-shrink-0">
            <img src={`/avatars/${p}_base.webp`} alt="" loading="lazy" className="h-full w-full object-cover" style={{ objectPosition: "center 15%" }} />
          </span>
        ))}
      </div>
    </div>
  </Panel>
);

export const BriefVignette = () => (
  <Panel label="Interview Brief" num="06" testid="vignette-brief" className="h-full">
    <div className="space-y-2.5">
      {[
        "Your story for this role, in 60 seconds",
        "STAR stories to lead with",
        "Likely questions & your strongest answers",
        "Day-of reminders and closing questions",
      ].map((t) => (
        <div key={t} className="flex items-start gap-2.5">
          <span className="mt-0.5 h-4 w-4 rounded bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
            <Check className="h-2.5 w-2.5 text-emerald-400" strokeWidth={3} />
          </span>
          <span className="text-[12px] text-zinc-200 leading-snug">{t}</span>
        </div>
      ))}
    </div>
    <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold text-emerald-300">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Built from your entire journey
    </div>
  </Panel>
);

export const ReadinessGauge = ({ score = 78, size = 200 }) => {
  const r = 84;
  const circ = Math.PI * r;
  const filled = (score / 100) * circ;
  return (
    <div data-testid="vignette-readiness-gauge" className="relative flex flex-col items-center" style={{ width: size }}>
      <svg viewBox="0 0 200 110" width={size} height={size * 0.55}>
        <path d="M 16 104 A 84 84 0 0 1 184 104" fill="none" stroke="#27272a" strokeWidth="12" strokeLinecap="round" />
        <path d="M 16 104 A 84 84 0 0 1 184 104" fill="none" stroke="#f59e0b" strokeWidth="12" strokeLinecap="round"
              strokeDasharray={`${filled} ${circ}`} />
      </svg>
      <div className="absolute inset-x-0 top-[42%] text-center">
        <div className="text-4xl font-semibold text-zinc-50 tabular-nums tracking-tight">{score}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Interview Readiness</div>
      </div>
    </div>
  );
};

export const ReadinessVignette = () => (
  <Panel label="Interview Readiness Score" num="05" testid="vignette-readiness" className="h-full flex flex-col">
    <div className="flex justify-center pt-2">
      <ReadinessGauge score={78} size={190} />
    </div>
    <div className="mt-5 grid grid-cols-2 gap-2">
      {[["Communication", 80], ["Structure", 71], ["Job Fit", 84], ["Confidence", 75]].map(([k, v]) => (
        <div key={k} className="rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2 flex items-center justify-between">
          <span className="text-[11px] text-zinc-400">{k}</span>
          <span className="text-[12px] font-semibold text-zinc-100 tabular-nums">{v}</span>
        </div>
      ))}
    </div>
    <div className="mt-4 inline-flex self-start items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold text-amber-300">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Nearly Ready · keep practicing
    </div>
  </Panel>
);

export const FileIntakeChip = () => (
  <div className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/70 px-3 py-1.5">
    <FileText className="h-3.5 w-3.5 text-amber-400" />
    <span className="text-[11px] text-zinc-300 font-mono">resume.pdf + job-description</span>
  </div>
);

/* ---- Hero product story: one connected Intuiterview experience ---- */

const FlowConnector = () => (
  <div className="flex flex-col items-center py-1" aria-hidden="true">
    <div className="w-px h-2.5 bg-zinc-800" />
    <div className="h-1.5 w-1.5 rounded-full bg-amber-500/70" />
    <div className="w-px h-2.5 bg-zinc-800" />
  </div>
);

const DocChip = ({ name }) => (
  <div className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-950/70 px-2.5 py-1.5">
    <FileText className="h-3 w-3 text-amber-400" />
    <span className="text-[10px] text-zinc-300 font-mono">{name}</span>
  </div>
);

const HeroBar = ({ label, value }) => (
  <div>
    <div className="flex justify-between text-[10px] mb-1">
      <span className="text-zinc-400">{label}</span>
      <span className="text-zinc-200 font-semibold tabular-nums">{value}</span>
    </div>
    <div className="h-1 rounded-full bg-zinc-800">
      <div className="h-1 rounded-full bg-amber-500/80" style={{ width: `${value}%` }} />
    </div>
  </div>
);

const StoryCardHeader = ({ children, muted }) => (
  <div className={`text-[9px] uppercase tracking-[0.18em] mb-3 ${muted ? "text-zinc-600" : "text-zinc-500"}`}>{children}</div>
);

export const HeroProductStory = () => (
  <div data-testid="hero-product-story" className="relative">
    <div className="absolute -inset-8 bg-amber-500/[0.04] blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
    <div className="relative rounded-3xl border border-zinc-800 bg-gradient-to-b from-zinc-900/90 to-zinc-950 shadow-[0_28px_90px_-28px_rgba(0,0,0,0.85)] p-4 sm:p-5">
      {/* window chrome */}
      <div className="flex items-center gap-2 mb-4 px-1">
        <span className="h-2 w-2 rounded-full bg-zinc-700" />
        <span className="h-2 w-2 rounded-full bg-zinc-700" />
        <span className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 ml-2">Intuiterview · Your interview workspace</span>
      </div>

      {/* Supporting: your two documents */}
      <div data-testid="hero-story-intake" className="flex items-center justify-center gap-2">
        <DocChip name="resume.pdf" />
        <span className="text-zinc-600 text-xs">+</span>
        <DocChip name="job-description.pdf" />
      </div>

      <FlowConnector />

      {/* Secondary: alignment · Tertiary: predicted questions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div data-testid="hero-story-alignment" className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3.5">
          <StoryCardHeader>Stand Out</StoryCardHeader>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 flex-shrink-0 rounded-full border-2 border-amber-500/70 flex items-center justify-center">
              <span className="text-sm font-semibold text-amber-400 tabular-nums">78</span>
            </div>
            <div>
              <div className="text-[12px] text-zinc-100 font-medium leading-tight">Alignment Score</div>
              <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">Your resume vs. this role</div>
            </div>
          </div>
          <div className="space-y-2.5">
            <HeroBar label="ATS keyword coverage" value={82} />
            <HeroBar label="Role & skills match" value={74} />
          </div>
          <div className="mt-3 font-mono text-[9px] text-zinc-500 bg-zinc-950/60 border border-zinc-800 rounded-md px-2 py-1.5 truncate">
            missing: <span className="text-amber-400">stakeholder management</span> <span className="text-zinc-600">+2 more</span>
          </div>
        </div>

        <div data-testid="hero-story-questions" className="rounded-xl border border-zinc-800/80 bg-zinc-950/50 p-3.5">
          <StoryCardHeader muted>Predicted Interview Questions</StoryCardHeader>
          <ul className="space-y-2">
            {[
              { tag: "From your resume", q: "Your resume says you grew activation 32% at Loopwork — walk me through what actually drove that number." },
              { tag: "Resume × job description", q: "This role owns a B2B roadmap, but most of your experience is B2C — how would you adapt your discovery process?" },
              { tag: "Career signal", q: "You moved from engineering into product in 2023 — what convinced you, and how does that background shape your PM style?" },
            ].map(({ tag, q }, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="mt-px h-4 w-4 flex-shrink-0 rounded bg-zinc-800/70 text-[9px] text-amber-400 font-semibold flex items-center justify-center tabular-nums">{i + 1}</span>
                <span className="min-w-0">
                  <span className="block text-[7px] uppercase tracking-[0.16em] text-amber-400/80 mb-0.5">{tag}</span>
                  <span className="block text-[11px] text-zinc-400 leading-snug">{q}</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2.5 text-[8px] uppercase tracking-[0.16em] text-zinc-600">
            Derived from your resume + this job description
          </div>
        </div>
      </div>

      <FlowConnector />

      {/* Practice strip: AI mock interview */}
      <div data-testid="hero-story-mock" className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/50 px-3.5 py-2.5">
        <span className="h-7 w-7 flex-shrink-0 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-center">
          <Video className="h-3.5 w-3.5 text-zinc-400" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-zinc-200 font-medium leading-tight">AI Mock Interview</div>
          <div className="text-[10px] text-zinc-500 leading-tight truncate">Answer out loud, on camera, replay with coaching</div>
        </div>
        <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-zinc-500 flex-shrink-0">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" /> Rec
        </span>
      </div>

      <FlowConnector />

      {/* Primary: Interview Readiness */}
      <div data-testid="hero-story-readiness" className="rounded-2xl border border-amber-500/25 bg-gradient-to-b from-zinc-900/90 to-zinc-950/80 p-4 shadow-[0_16px_50px_-20px_rgba(245,158,11,0.15)]">
        <StoryCardHeader>Interview Readiness Score</StoryCardHeader>
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <div className="flex flex-col items-center flex-shrink-0">
            <ReadinessGauge score={78} size={160} />
            <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-amber-400/90">
              <span className="h-1 w-1 rounded-full bg-amber-400" /> Good progress, keep practicing
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 w-full">
            {[["Communication", 80], ["Structure", 71], ["Job Fit", 84], ["Confidence", 75]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-zinc-800 bg-zinc-950/60 px-2.5 py-2 flex items-center justify-between">
                <span className="text-[10px] text-zinc-400">{k}</span>
                <span className="text-[11px] font-semibold text-zinc-100 tabular-nums">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);
